import {
  createUnvalidatedValidationSummary,
  type CoasterProject,
  type TrackBehavior,
  type TrackNode,
  type TrackPieceParameters,
  type Vec3,
} from '../schema/index.ts';
import { makeOrderedSection, PHASE_2_NODE_LIMIT } from './topology.ts';
import { newStableId, setSectionBehavior, type TrackOperationResult } from './operations.ts';
import { assignPieceGroup } from './piece-groups.ts';
import { assessCandidateProject, type CandidatePreflight } from './validation.ts';
import {
  generateSemanticElementPoints,
  isSemanticInversion,
  type SemanticControlPoint,
  type SemanticShapeElementId,
} from './semantic-elements.ts';
import {
  appendCompositeModularPiece,
  appendModularPiece,
  legacyDefinitionToComposite,
  modularPieceLabel,
  placeModularStation,
  type ModularPieceDefinitionId,
  type TrackType,
} from './modular-track.ts';

export type ElementPresetId =
  | 'station'
  | 'straight'
  | 'turn_left'
  | 'turn_right'
  | 'slope_up'
  | 'slope_down'
  | 'bank_left'
  | 'bank_right'
  | 'bank_level'
  | 'hill'
  | 'drop'
  | 'lift'
  | 'launch'
  | 'brake'
  | 'loop'
  | 'loop_left'
  | 'loop_right'
  | 'roll_left'
  | 'roll_right'
  | 'corkscrew_left'
  | 'corkscrew_right'
  | 'photo';

export type StationPlacement = 'near' | 'center' | 'far';

export interface ElementPresetDefinition {
  readonly id: ElementPresetId;
  readonly label: string;
  readonly category: 'shape' | 'system' | 'moment';
  readonly description: string;
  readonly hidden?: boolean;
}

export interface PresetCandidate {
  readonly preset: ElementPresetDefinition;
  readonly project: CoasterProject;
  readonly message: string;
  readonly warnings: readonly string[];
  readonly affectedNodeIds: readonly string[];
  readonly affectedSectionIds: readonly string[];
  readonly preflight: CandidatePreflight;
  readonly focusNodeId?: string;
}

export const ELEMENT_PRESETS: readonly ElementPresetDefinition[] = [
  {
    id: 'station',
    label: 'Station',
    category: 'system',
    description: 'Start and finish the ride.',
  },
  {
    id: 'straight',
    label: 'Straight',
    category: 'shape',
    description: 'Add a clean straight section.',
  },
  {
    id: 'turn_left',
    label: 'Left Turn',
    category: 'shape',
    description: 'Curve left with safe spacing.',
  },
  {
    id: 'turn_right',
    label: 'Right Turn',
    category: 'shape',
    description: 'Curve right with safe spacing.',
  },
  {
    id: 'slope_up',
    label: 'Slope',
    category: 'shape',
    description: 'Choose the track angle you want next.',
  },
  {
    id: 'slope_down',
    label: 'Slope',
    category: 'shape',
    description: 'Choose the track angle you want next.',
    hidden: true,
  },
  {
    id: 'bank_left',
    label: 'Bank',
    category: 'shape',
    description: 'Choose the track bank you want next.',
  },
  {
    id: 'bank_right',
    label: 'Bank',
    category: 'shape',
    description: 'Choose the track bank you want next.',
    hidden: true,
  },
  {
    id: 'bank_level',
    label: 'Bank',
    category: 'shape',
    description: 'Choose the track bank you want next.',
    hidden: true,
  },
  {
    id: 'hill',
    label: 'Legacy Hill',
    category: 'shape',
    description: 'Legacy freeform hill.',
    hidden: true,
  },
  {
    id: 'drop',
    label: 'Legacy Drop',
    category: 'shape',
    description: 'Legacy freeform drop.',
    hidden: true,
  },
  {
    id: 'lift',
    label: 'Lift Hill',
    category: 'system',
    description: 'Climb with a powered chain.',
  },
  { id: 'launch', label: 'Launch', category: 'system', description: 'Accelerate the train.' },
  { id: 'brake', label: 'Brakes', category: 'system', description: 'Control the approach speed.' },
  {
    id: 'loop',
    label: 'Loop Right',
    category: 'shape',
    description: 'Legacy right-offset vertical loop.',
    hidden: true,
  },
  {
    id: 'loop_left',
    label: 'Loop Left',
    category: 'shape',
    description: 'Add a left-offset vertical loop.',
  },
  {
    id: 'loop_right',
    label: 'Loop Right',
    category: 'shape',
    description: 'Add a right-offset vertical loop.',
  },
  {
    id: 'roll_left',
    label: 'Left Roll',
    category: 'shape',
    description: 'Roll one full turn left.',
  },
  {
    id: 'roll_right',
    label: 'Right Roll',
    category: 'shape',
    description: 'Roll one full turn right.',
  },
  {
    id: 'corkscrew_left',
    label: 'Left Corkscrew',
    category: 'shape',
    description: 'Twist through a compact left corkscrew.',
  },
  {
    id: 'corkscrew_right',
    label: 'Right Corkscrew',
    category: 'shape',
    description: 'Twist through a compact right corkscrew.',
  },
  {
    id: 'photo',
    label: 'Photo',
    category: 'moment',
    description: 'Mark the on-ride photo moment.',
  },
] as const;

function touch(project: CoasterProject): CoasterProject {
  project.metadata.updatedAt = new Date().toISOString();
  project.validation = createUnvalidatedValidationSummary();
  return project;
}

function definition(id: ElementPresetId): ElementPresetDefinition {
  return ELEMENT_PRESETS.find((preset) => preset.id === id)!;
}

function finalizeCandidate(
  base: CoasterProject,
  candidate: Omit<PresetCandidate, 'preflight'>,
): PresetCandidate {
  return {
    ...candidate,
    preflight: assessCandidateProject(
      base,
      candidate.project,
      candidate.affectedNodeIds,
      candidate.affectedSectionIds,
    ),
  };
}

function systemBehavior(id: ElementPresetId): TrackBehavior | null {
  switch (id) {
    case 'station':
      return { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 };
    case 'lift':
      return { kind: 'lift', targetSpeedMps: 5 };
    case 'launch':
      return { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 };
    case 'brake':
      return { kind: 'brake', targetSpeedMps: 9, decelerationMps2: 4 };
    case 'photo':
      return { kind: 'photo' };
    default:
      return null;
  }
}

function blankStation(
  project: CoasterProject,
  placement: StationPlacement = 'near',
): PresetCandidate {
  if (project.track.geometryKind === 'modular_piecewise_bezier') {
    const start =
      placement === 'center'
        ? { x: -12, z: 0 }
        : placement === 'far'
          ? { x: -30, z: -20 }
          : { x: -30, z: 20 };
    const result = placeModularStation(project, { x: start.x, y: 4, z: start.z });
    if (!result.ok) throw new Error(`Could not create modular station preview: ${result.message}`);
    return finalizeCandidate(project, {
      preset: definition('station'),
      project: touch(result.project),
      message: 'Station placed. Add track from the mint exit marker.',
      warnings: [],
      affectedNodeIds: result.project.track.nodes.map((node) => node.id),
      affectedSectionIds: result.project.track.sections.map((section) => section.id),
      focusNodeId: result.project.track.nodes.at(-1)!.id,
    });
  }
  const next = structuredClone(project);
  const start =
    placement === 'center'
      ? { x: -12, z: 0 }
      : placement === 'far'
        ? { x: -30, z: -20 }
        : { x: -30, z: 20 };
  const positions: Vec3[] = [0, 8, 16, 24].map((offset) => ({
    x: start.x + offset,
    y: 4,
    z: start.z,
  }));
  next.track.closed = false;
  next.track.nodes = positions.map((position) => ({
    id: newStableId('node'),
    position,
    bankDegrees: 0,
    rollWindingDegrees: 0,
  }));
  next.track.sections = next.track.nodes
    .slice(0, -1)
    .map((node, index) =>
      makeOrderedSection(
        newStableId('section'),
        node.id,
        next.track.nodes[index + 1]!.id,
        index < 2
          ? { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 }
          : { kind: 'normal' },
      ),
    );
  next.editor.pieceGroups = [];
  const grouped = assignPieceGroup(
    next,
    next.track.sections.map((section) => section.id),
    'station',
    { length: 'long' },
  );
  return finalizeCandidate(project, {
    preset: definition('station'),
    project: touch(grouped),
    message: 'Station placed. Add track from the mint exit marker.',
    warnings: [],
    affectedNodeIds: next.track.nodes.map((node) => node.id),
    affectedSectionIds: next.track.sections.map((section) => section.id),
    focusNodeId: next.track.nodes.at(-1)!.id,
  });
}

function vectorLength(vector: Vec3): number {
  return Math.hypot(vector.x, vector.y, vector.z);
}

function localFrame(from: Vec3, to: Vec3): { forward: Vec3; right: Vec3; distance: number } {
  const delta = { x: to.x - from.x, y: 0, z: to.z - from.z };
  const rawDistance = vectorLength(delta);
  const distance = Math.max(8, rawDistance);
  const forward =
    rawDistance > 0.001
      ? { x: delta.x / rawDistance, y: 0, z: delta.z / rawDistance }
      : { x: 1, y: 0, z: 0 };
  return { forward, right: { x: forward.z, y: 0, z: -forward.x }, distance };
}

interface AppendPoint {
  readonly forward: number;
  readonly side?: number;
  readonly height?: number;
  readonly bank?: number;
}

function isShapeElement(id: ElementPresetId): id is SemanticShapeElementId {
  return (
    id === 'straight' ||
    id === 'turn_left' ||
    id === 'turn_right' ||
    id === 'hill' ||
    id === 'drop' ||
    id === 'loop' ||
    id === 'loop_left' ||
    id === 'loop_right' ||
    id === 'roll_left' ||
    id === 'roll_right' ||
    id === 'corkscrew_left' ||
    id === 'corkscrew_right'
  );
}

function toAppendPoint(point: SemanticControlPoint): AppendPoint {
  return {
    forward: point.forwardM,
    side: point.sideM,
    height: point.heightM,
    bank: point.bankDeltaDegrees,
  };
}

function appendPoints(id: ElementPresetId): readonly AppendPoint[] {
  if (isShapeElement(id)) return generateSemanticElementPoints(id).map(toAppendPoint);
  switch (id) {
    case 'station':
      return [{ forward: 6 }, { forward: 12 }];
    case 'launch':
    case 'brake':
    case 'photo':
      return [{ forward: 6 }, { forward: 12 }];
    case 'lift':
      return [
        { forward: 5, height: 2.5 },
        { forward: 10, height: 6 },
        { forward: 15, height: 10 },
        { forward: 20, height: 14 },
      ];
    case 'slope_up':
    case 'slope_down':
    case 'bank_left':
    case 'bank_right':
    case 'bank_level':
      return [];
  }
}

function appendToOpenEnd(
  project: CoasterProject,
  id: ElementPresetId,
  parameters: TrackPieceParameters = {},
  trackType?: TrackType,
): PresetCandidate | TrackOperationResult {
  if (project.track.geometryKind === 'modular_piecewise_bezier') {
    const legacyComposite = legacyDefinitionToComposite(id as ModularPieceDefinitionId);
    const shapeId = trackType ? legacyComposite.shapeId : (id as ModularPieceDefinitionId);
    const result = trackType
      ? appendCompositeModularPiece(project, legacyComposite.shapeId, parameters, trackType)
      : appendModularPiece(project, shapeId, parameters);
    if (!result.ok) return result;
    const originalNodeIds = new Set(project.track.nodes.map((node) => node.id));
    const originalSectionIds = new Set(project.track.sections.map((section) => section.id));
    return finalizeCandidate(project, {
      preset: definition(id),
      project: touch(result.project),
      message: result.project.track.closed
        ? `${modularPieceLabel(shapeId)} added. Circuit closed.`
        : `${modularPieceLabel(shapeId)} added. Keep building from the mint exit marker.`,
      warnings: isSemanticInversion(id) ? ['Run Ride Check before riding this inversion.'] : [],
      affectedNodeIds: result.project.track.nodes
        .filter((node) => !originalNodeIds.has(node.id))
        .map((node) => node.id),
      affectedSectionIds: result.project.track.sections
        .filter((section) => !originalSectionIds.has(section.id))
        .map((section) => section.id),
      focusNodeId: result.project.track.nodes.at(-1)!.id,
    });
  }
  if (project.track.closed || project.track.nodes.length < 2)
    return { ok: false, code: 'not_open', message: 'Start with a station first.' };
  const points = appendPoints(id);
  if (project.track.nodes.length + points.length > PHASE_2_NODE_LIMIT)
    return {
      ok: false,
      code: 'node_limit',
      message: `This editor supports up to ${PHASE_2_NODE_LIMIT} shape handles.`,
    };
  let next = structuredClone(project);
  let end = next.track.nodes.at(-1)!;
  let beforeEnd = next.track.nodes.at(-2)!;
  if (
    Math.hypot(end.position.x - beforeEnd.position.x, end.position.z - beforeEnd.position.z) < 0.5
  )
    return {
      ok: false,
      code: 'degenerate_endpoint',
      message: 'Move the track exit away from the previous handle before adding a piece.',
    };
  const inversion = isSemanticInversion(id);
  const horizontalDistance = Math.hypot(
    end.position.x - beforeEnd.position.x,
    end.position.z - beforeEnd.position.z,
  );
  const pitchDegrees =
    (Math.atan2(end.position.y - beforeEnd.position.y, Math.max(0.001, horizontalDistance)) * 180) /
    Math.PI;
  if (inversion && Math.abs(end.rollWindingDegrees) > 0.001)
    return {
      ok: false,
      code: 'inversion_entry_winding',
      message: 'Add a level piece after the previous inversion before placing this element.',
    };
  if (inversion && Math.abs(pitchDegrees) > 60)
    return {
      ok: false,
      code: 'inversion_entry_pitch',
      message: 'The track is too steep here. Add a gentler slope piece first.',
    };

  const transitionGroups: Array<{
    definitionId: 'bank_out' | 'slope_to_level';
    sectionIds: string[];
  }> = [];
  const appendTransition = (definitionId: 'bank_out' | 'slope_to_level', nodes: TrackNode[]) => {
    const chain = [next.track.nodes.at(-1)!, ...nodes];
    const sections = chain.slice(0, -1).map((node, index) =>
      makeOrderedSection(newStableId('section'), node.id, chain[index + 1]!.id, {
        kind: 'normal',
      }),
    );
    next.track.nodes.push(...nodes);
    next.track.sections.push(...sections);
    transitionGroups.push({ definitionId, sectionIds: sections.map((section) => section.id) });
  };

  if (inversion && Math.abs(end.bankDegrees) > 0.001) {
    const frame = localFrame(beforeEnd.position, end.position);
    const slopePerMeter = (end.position.y - beforeEnd.position.y) / Math.max(frame.distance, 0.001);
    appendTransition(
      'bank_out',
      [6, 12].map((distance, index) => ({
        id: newStableId('node'),
        position: {
          x: end.position.x + frame.forward.x * distance,
          y: end.position.y + slopePerMeter * distance,
          z: end.position.z + frame.forward.z * distance,
        },
        bankDegrees: index === 0 ? end.bankDegrees * 0.5 : 0,
        rollWindingDegrees: 0,
      })),
    );
    beforeEnd = next.track.nodes.at(-2)!;
    end = next.track.nodes.at(-1)!;
  }

  if (inversion && Math.abs(pitchDegrees) > 0.5) {
    const frame = localFrame(beforeEnd.position, end.position);
    const rise = Math.tan((pitchDegrees * Math.PI) / 180) * 6;
    appendTransition('slope_to_level', [
      {
        id: newStableId('node'),
        position: {
          x: end.position.x + frame.forward.x * 6,
          y: end.position.y + rise * 0.65,
          z: end.position.z + frame.forward.z * 6,
        },
        bankDegrees: 0,
        rollWindingDegrees: 0,
      },
      {
        id: newStableId('node'),
        position: {
          x: end.position.x + frame.forward.x * 12,
          y: end.position.y + rise * 0.8,
          z: end.position.z + frame.forward.z * 12,
        },
        bankDegrees: 0,
        rollWindingDegrees: 0,
      },
    ]);
    beforeEnd = next.track.nodes.at(-2)!;
    end = next.track.nodes.at(-1)!;
  }

  const frame = localFrame(beforeEnd.position, end.position);
  const previousBehavior = next.track.sections.at(-1)?.behavior.kind;
  const entrySlopePerMeter =
    (end.position.y - beforeEnd.position.y) /
    Math.max(
      0.001,
      Math.hypot(end.position.x - beforeEnd.position.x, end.position.z - beforeEnd.position.z),
    );
  const continuesLift = id === 'lift' && previousBehavior === 'lift';
  const generated = points.map<TrackNode>((point) => ({
    id: newStableId('node'),
    position: {
      x: end.position.x + frame.forward.x * point.forward + frame.right.x * (point.side ?? 0),
      y: Math.max(
        1,
        Math.min(
          65,
          end.position.y +
            (continuesLift ? entrySlopePerMeter * point.forward : (point.height ?? 0)),
        ),
      ),
      z: end.position.z + frame.forward.z * point.forward + frame.right.z * (point.side ?? 0),
    },
    bankDegrees: inversion
      ? end.bankDegrees
      : Math.max(-90, Math.min(90, end.bankDegrees + (point.bank ?? 0))),
    rollWindingDegrees: inversion
      ? end.rollWindingDegrees + (point.bank ?? 0)
      : end.rollWindingDegrees,
  }));
  const behavior = systemBehavior(id) ?? { kind: 'normal' as const };
  const chain = [end, ...generated];
  const additions = chain
    .slice(0, -1)
    .map((node, index) =>
      makeOrderedSection(
        newStableId('section'),
        node.id,
        chain[index + 1]!.id,
        structuredClone(behavior),
      ),
    );
  next.track.nodes.push(...generated);
  next.track.sections.push(...additions);
  for (const transition of transitionGroups)
    next = assignPieceGroup(next, transition.sectionIds, transition.definitionId, {
      length: 'medium',
    });
  const grouped = assignPieceGroup(
    next,
    additions.map((section) => section.id),
    id,
    { length: 'medium', ...(isSemanticInversion(id) ? { size: 'medium' as const } : {}) },
  );
  return finalizeCandidate(project, {
    preset: definition(id),
    project: touch(grouped),
    message: `${definition(id).label} added. Keep building from the mint exit marker.`,
    warnings: inversion
      ? ['Inversions are advanced pieces. Check the highlighted preview and run Ride Check.']
      : [],
    affectedNodeIds: [end.id, ...generated.map((node) => node.id)],
    affectedSectionIds: additions.map((section) => section.id),
    focusNodeId: generated.at(-1)!.id,
  });
}

function localPoints(id: ElementPresetId, chordLengthM: number): readonly SemanticControlPoint[] {
  return isShapeElement(id)
    ? generateSemanticElementPoints(id, {
        chordLengthM,
        includeExit: false,
        replacement: true,
      })
    : [];
}

function replacePieceShape(
  project: CoasterProject,
  sectionIds: readonly string[],
  id: ElementPresetId,
): TrackOperationResult {
  const sectionIndexes = sectionIds
    .map((sectionId) => project.track.sections.findIndex((section) => section.id === sectionId))
    .sort((left, right) => left - right);
  if (!sectionIndexes.length || sectionIndexes.some((index) => index < 0))
    return { ok: false, code: 'missing_section', message: 'Select a track piece first.' };
  const firstSectionIndex = sectionIndexes[0]!;
  const lastSectionIndex = sectionIndexes.at(-1)!;
  const contiguous = sectionIndexes.every((index, offset) => index === firstSectionIndex + offset);
  if (!contiguous || lastSectionIndex >= project.track.sections.length - 1)
    return {
      ok: false,
      code: 'unsupported_wrapped_piece',
      message: 'Choose a regular track piece that does not cross the station seam.',
    };
  const next = structuredClone(project);
  const section = next.track.sections[firstSectionIndex]!;
  const from = next.track.nodes[firstSectionIndex]!;
  const to = next.track.nodes[lastSectionIndex + 1]!;
  const frame = localFrame(from.position, to.position);
  const points = localPoints(id, frame.distance);
  const inversion = isSemanticInversion(id);
  if (
    inversion &&
    (Math.abs(from.bankDegrees) > 0.001 ||
      Math.abs(to.bankDegrees) > 0.001 ||
      Math.abs(from.rollWindingDegrees) > 0.001 ||
      Math.abs(to.rollWindingDegrees) > 0.001 ||
      Math.abs(to.position.y - from.position.y) > 0.5)
  )
    return {
      ok: false,
      code: 'incompatible_inversion_entry',
      message: 'Choose a level, unbanked straight piece before adding this element.',
    };
  if (project.track.nodes.length + points.length > PHASE_2_NODE_LIMIT)
    return {
      ok: false,
      code: 'node_limit',
      message: `This editor supports up to ${PHASE_2_NODE_LIMIT} track points.`,
    };
  const generated = points.map<TrackNode>((point) => ({
    id: newStableId('node'),
    position: {
      x: from.position.x + frame.forward.x * point.forwardM + frame.right.x * point.sideM,
      y: Math.max(
        1,
        Math.min(
          65,
          from.position.y + (to.position.y - from.position.y) * point.progress + point.heightM,
        ),
      ),
      z: from.position.z + frame.forward.z * point.forwardM + frame.right.z * point.sideM,
    },
    bankDegrees: inversion
      ? from.bankDegrees
      : Math.max(-90, Math.min(90, from.bankDegrees + point.bankDeltaDegrees)),
    rollWindingDegrees: inversion
      ? from.rollWindingDegrees + point.bankDeltaDegrees
      : from.rollWindingDegrees,
  }));
  const internalNodeCount = lastSectionIndex - firstSectionIndex;
  next.track.nodes.splice(firstSectionIndex + 1, internalNodeCount, ...generated);
  next.track.sections.splice(firstSectionIndex, sectionIndexes.length);
  const chain = [from, ...generated, to];
  const replacementSections = chain
    .slice(0, -1)
    .map((node, index) =>
      makeOrderedSection(
        sectionIds[index] ?? newStableId('section'),
        node.id,
        chain[index + 1]!.id,
        index === 0 ? structuredClone(section.behavior) : { kind: 'normal' },
      ),
    );
  next.track.sections.splice(firstSectionIndex, 0, ...replacementSections);
  const retainedSectionIds = new Set(replacementSections.map((replacement) => replacement.id));
  const fallbackSectionId = replacementSections[0]!.id;
  const removedSectionIds = new Set(
    sectionIds.filter((sectionId) => !retainedSectionIds.has(sectionId)),
  );
  next.supports.suppressedSectionIds = next.supports.suppressedSectionIds.map((sectionId) =>
    removedSectionIds.has(sectionId) ? fallbackSectionId : sectionId,
  );
  next.supports.manual = next.supports.manual.map((support) =>
    removedSectionIds.has(support.sectionId)
      ? { ...support, sectionId: fallbackSectionId }
      : support,
  );
  next.highlights = next.highlights.map((highlight) =>
    removedSectionIds.has(highlight.sectionId)
      ? { ...highlight, sectionId: fallbackSectionId }
      : highlight,
  );
  return {
    ok: true,
    project: touch(
      assignPieceGroup(
        next,
        replacementSections.map((replacement) => replacement.id),
        id,
        { length: 'medium', ...(isSemanticInversion(id) ? { size: 'medium' as const } : {}) },
      ),
    ),
    message: `${definition(id).label} ready.`,
  };
}

export function buildPresetCandidate(
  project: CoasterProject,
  id: ElementPresetId,
  selectedSectionId: string | null,
  options: {
    stationPlacement?: StationPlacement;
    parameters?: TrackPieceParameters;
    trackType?: TrackType;
  } = {},
): TrackOperationResult | PresetCandidate {
  if (id === 'station' && project.track.nodes.length === 0)
    return blankStation(project, options.stationPlacement);
  if (!project.track.closed)
    return appendToOpenEnd(project, id, options.parameters, options.trackType);
  if (!selectedSectionId)
    return { ok: false, code: 'missing_section', message: 'Select a track section first.' };
  const selectedPiece = project.editor.pieceGroups.find((piece) =>
    piece.sectionIds.includes(selectedSectionId),
  );
  const selectedPieceSectionIds = selectedPiece?.sectionIds ?? [selectedSectionId];
  const behavior = systemBehavior(id);
  if (behavior) {
    let updated = structuredClone(project);
    for (const sectionId of selectedPieceSectionIds) {
      const result = setSectionBehavior(updated, sectionId, behavior);
      if (!result.ok) return result;
      updated = result.project;
    }
    return finalizeCandidate(project, {
      preset: definition(id),
      project: assignPieceGroup(updated, selectedPieceSectionIds, id, null),
      message: `${definition(id).label} applied to the selected piece.`,
      warnings: [],
      affectedNodeIds: [],
      affectedSectionIds: selectedPieceSectionIds,
    });
  }
  const result = replacePieceShape(project, selectedPieceSectionIds, id);
  if (!result.ok) return result;
  const firstSelectedSection = selectedPieceSectionIds
    .map((sectionId) => project.track.sections.find((candidate) => candidate.id === sectionId))
    .find(Boolean);
  const originalNodeIds = new Set(project.track.nodes.map((node) => node.id));
  const originalSectionIds = new Set(project.track.sections.map((candidate) => candidate.id));
  return finalizeCandidate(project, {
    preset: definition(id),
    project: result.project,
    message: result.message,
    warnings: isSemanticInversion(id) ? ['Run Ride Check before riding this inversion.'] : [],
    affectedNodeIds: [
      ...(firstSelectedSection
        ? [firstSelectedSection.fromNodeId, firstSelectedSection.toNodeId]
        : []),
      ...result.project.track.nodes
        .filter((node) => !originalNodeIds.has(node.id))
        .map((node) => node.id),
    ],
    affectedSectionIds: result.project.track.sections
      .filter(
        (candidate) => candidate.id === selectedSectionId || !originalSectionIds.has(candidate.id),
      )
      .map((candidate) => candidate.id),
  });
}
